/**
 * FN-FE-12 — Projektek, Feladatok, Időnaplózás
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { get, post, patch, del } from '../../lib/api';
import { getAccessToken } from '../../lib/auth';
import { PageHeader, Button, Badge, Card, Input, Modal, EmptyState, Tabs } from '../../components/ui';
import { useFKeyBar } from '../../components/layout/FKeyBar';
import { useToast } from '../../components/ui/Toast';
import PartnerAutocomplete from '../../components/PartnerAutocomplete';
import PostIssueModal from '../../components/PostIssueModal';

// ── Types ──

interface Project {
  id: string; name: string; project_code: string; partner_name: string; owner_name: string;
  status: string; start_date: string | null; due_date: string | null; description: string | null;
  task_count: number; done_count: number; partner_id: string; owner_user_id: string;
  color: string | null; created_at: string; vat_rate: number; is_billable: boolean; hourly_rate: number | null;
}
interface ProjectDetail extends Project { members: Member[]; milestones: Milestone[]; taskCount: number; doneCount: number; }
interface Task {
  id: string; title: string; description: string | null; status: string; priority: string;
  start_date: string | null; due_date: string | null; assignee_name: string | null; assignee_user_id: string | null;
  estimated_hours: number | null; position: number; milestone_id: string | null; project_id: string; hourly_rate: number | null;
}
interface Milestone { id: string; name: string; due_date: string | null; description: string | null; position: number; }
interface Member { id: string; user_id: string; user_name: string; email: string; role: string; hourly_rate: number | null; }
interface KanbanColumn { status: string; title: string; tasks: Task[]; count: number; }
interface CalendarEvent { taskId: string; title: string; date: string; priority: string; status: string; isOverdue: boolean; }
interface User { id: string; name: string; email: string; }
interface TimeEntry {
  id: string; user_id: string; user_name: string; task_id: string | null;
  entry_date: string; duration_minutes: number; description: string | null;
  is_billable: boolean; is_billed: boolean; hourly_rate: number | null;
}
interface ActiveTimer {
  user_id: string; project_id: string; task_id: string | null;
  started_at: string; paused_at: string | null; paused_seconds: number; project_name: string;
}
interface Share {
  id: string; share_token: string; valid_until: string | null; is_revoked: boolean;
  include_hourly_rate: boolean; include_descriptions: boolean; created_at: string; view_count: number;
}
interface BillableEntry {
  id: string; user_name: string; task_name: string | null; project_name: string;
  entry_date: string; duration_minutes: number; hourly_rate: number | null; is_billable: boolean;
}

// ── Constants ──

const STATUS_MAP: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'error' | 'info' }> = {
  planning: { label: 'Tervezés', variant: 'default' }, planned: { label: 'Tervezés', variant: 'default' },
  active: { label: 'Aktív', variant: 'success' }, on_hold: { label: 'Szünetel', variant: 'warning' },
  completed: { label: 'Befejezve', variant: 'info' }, cancelled: { label: 'Törölve', variant: 'error' },
};
const TASK_STATUS_MAP: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'error' | 'info' }> = {
  backlog: { label: 'Várólistán', variant: 'default' }, todo: { label: 'TODO', variant: 'default' },
  in_progress: { label: 'Folyamatban', variant: 'info' }, review: { label: 'Ellenőrzés', variant: 'warning' },
  done: { label: 'Kész', variant: 'success' }, cancelled: { label: 'Törölve', variant: 'error' },
};
const PRIORITY_MAP: Record<string, { label: string; color: string }> = {
  low: { label: 'Alacsony', color: '#16A34A' }, medium: { label: 'Közepes', color: '#2563EB' },
  high: { label: 'Magas', color: '#D97706' }, urgent: { label: 'Kritikus', color: '#DC2626' },
};
const GANTT_COLORS: Record<string, string> = {
  todo: '#CBD5E1', in_progress: '#60A5FA', review: '#FBBF24', done: '#34D399', cancelled: '#FCA5A5',
};

// ── Helpers ──

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' });
}
function daysRemaining(d: string | null) {
  if (!d) return null;
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const due = new Date(d); due.setHours(0, 0, 0, 0);
  return Math.ceil((due.getTime() - now.getTime()) / 86400000);
}
function progressPercent(done: number, total: number) { return total > 0 ? Math.round((done / total) * 100) : 0; }
function fmtDur(min: number) {
  const h = Math.floor(min / 60); const m = min % 60;
  return h > 0 && m > 0 ? `${h}ó ${m}p` : h > 0 ? `${h}ó` : `${m}p`;
}
function fmtElapsed(s: number) {
  return `${Math.floor(s / 3600)}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}
function deadlineColor(due: string | null) {
  const d = daysRemaining(due);
  if (d === null) return 'text-gray-400';
  return d < 0 ? 'text-red-600' : d <= 3 ? 'text-amber-600' : 'text-gray-500';
}

// ── Main Component ──

export default function ProjectsPage() {
  const { toast } = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'list' | 'cards'>('list');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [detailTab, setDetailTab] = useState(0);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [kanban, setKanban] = useState<KanbanColumn[]>([]);
  const [calendar, setCalendar] = useState<CalendarEvent[]>([]);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [taskView, setTaskView] = useState<'kanban' | 'list' | 'gantt' | 'calendar'>('kanban');

  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editProject, setEditProject] = useState<ProjectDetail | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [editMilestone, setEditMilestone] = useState<Milestone | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const dragTaskId = useRef<string | null>(null);
  const dragSourceStatus = useRef<string | null>(null);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (search) p.set('search', search);
      if (statusFilter) p.set('status', statusFilter);
      const data = await get<Project[]>(`/projects${p.toString() ? '?' + p : ''}`);
      setProjects(Array.isArray(data) ? data : (data as any).data || []);
    } catch { toast('Projektek betöltése sikertelen.', 'error'); }
    setLoading(false);
  }, [search, statusFilter]);

  const loadDetail = useCallback(async (id: string) => {
    try {
      const [p, t, ms, mb] = await Promise.all([
        get<ProjectDetail>(`/projects/${id}`),
        get<Task[]>(`/projects/${id}/tasks`),
        get<Milestone[]>(`/projects/${id}/milestones`).catch(() => [] as Milestone[]),
        get<Member[]>(`/projects/${id}/members`).catch(() => [] as Member[]),
      ]);
      setDetail(p); setTasks(Array.isArray(t) ? t : []); setMilestones(Array.isArray(ms) ? ms : []); setMembers(Array.isArray(mb) ? mb : []);
    } catch { toast('Projekt betöltése sikertelen.', 'error'); }
  }, []);

  const loadKanban = useCallback(async (id: string) => {
    try { const d = await get<{ columns: KanbanColumn[] }>(`/projects/${id}/tasks/kanban`); setKanban(d.columns || []); }
    catch (e: any) { if (e?.code === 'plan_feature_disabled') { setTaskView('list'); toast('Kanban Standard csomagtól érhető el.', 'warning'); } }
  }, []);

  const loadCalendar = useCallback(async (id: string, month: string) => {
    try { const d = await get<{ events: CalendarEvent[] }>(`/projects/${id}/tasks/calendar?month=${month}`); setCalendar(d.events || []); }
    catch (e: any) { if (e?.code === 'plan_feature_disabled') { setTaskView('list'); toast('Naptár Pro csomagtól érhető el.', 'warning'); } }
  }, []);

  const loadUsersAndPartners = useCallback(async () => {
    try { const u = await get<any>('/users').catch(() => []); setUsers(Array.isArray(u) ? u : u?.data || []); }
    catch { /* silent */ }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);
  useEffect(() => { if (detail && taskView === 'kanban') loadKanban(detail.id); }, [detail?.id, taskView]);
  useEffect(() => { if (detail && taskView === 'calendar') loadCalendar(detail.id, calendarMonth); }, [detail?.id, taskView, calendarMonth]);

  useFKeyBar(detail ? [
    { key: 'F2', label: 'Új feladat', action: () => { loadUsersAndPartners(); setEditTask(null); setShowTaskModal(true); } },
    { key: 'F3', label: 'Új mérföldkő', action: () => { setEditMilestone(null); setShowMilestoneModal(true); } },
    { key: 'F4', label: 'Nézet váltó', action: () => setTaskView(v => v === 'kanban' ? 'list' : v === 'list' ? 'gantt' : v === 'gantt' ? 'calendar' : 'kanban') },
    { key: 'F5', label: 'Frissít', action: () => { loadDetail(detail.id); if (taskView === 'kanban') loadKanban(detail.id); } },
    { key: 'F8', label: 'Vissza', action: () => { setDetail(null); setDetailTab(0); loadProjects(); } },
  ] : [
    { key: 'F2', label: 'Új projekt', action: () => { loadUsersAndPartners(); setEditProject(null); setShowProjectModal(true); } },
    { key: 'F3', label: 'Keresés', action: () => document.getElementById('proj-search')?.focus() },
    { key: 'F5', label: 'Frissít', action: loadProjects },
  ]);

  const handleDrop = async (targetStatus: string) => {
    const taskId = dragTaskId.current; const src = dragSourceStatus.current;
    if (!taskId || !detail || src === targetStatus) return;
    setKanban(prev => {
      const task = prev.flatMap(c => c.tasks).find(t => t.id === taskId); if (!task) return prev;
      return prev.map(col => {
        if (col.status === src) return { ...col, tasks: col.tasks.filter(t => t.id !== taskId), count: col.count - 1 };
        if (col.status === targetStatus) return { ...col, tasks: [...col.tasks, { ...task, status: targetStatus }], count: col.count + 1 };
        return col;
      });
    });
    try { await post(`/projects/${detail.id}/tasks/${taskId}/move`, { status: targetStatus, position: 999 }); await loadDetail(detail.id); if (taskView === 'kanban') loadKanban(detail.id); }
    catch { toast('Áthelyezés sikertelen.', 'error'); loadKanban(detail.id); }
  };

  // ── Detail View ──
  if (detail) {
    const pct = progressPercent(detail.doneCount, detail.taskCount);
    const daysLeft = daysRemaining(detail.due_date);
    const totalEst = tasks.reduce((s, t) => s + (t.estimated_hours || 0), 0);

    return (
      <div className="space-y-6">
        {/* Back + Header */}
        <div className="pb-2">
          <button onClick={() => { setDetail(null); setDetailTab(0); loadProjects(); }}
            className="text-sm text-gray-500 hover:text-[#1a2540] mb-4 inline-flex items-center gap-1">
            ← Vissza
          </button>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-bold text-[#1a2540]">{detail.name}</h1>
                <Badge variant={STATUS_MAP[detail.status]?.variant || 'default'}>{STATUS_MAP[detail.status]?.label || detail.status}</Badge>
                {detail.is_billable && <span style={{ fontSize: 11, background: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0', padding: '3px 9px', borderRadius: 20, fontWeight: 700 }}>Számlázható</span>}
              </div>
              <p className="text-sm text-gray-500 mt-1.5">{detail.project_code} · {detail.partner_name} · {detail.owner_name || '—'}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button size="sm" variant="ghost" onClick={() => { loadUsersAndPartners(); setEditProject(detail); setShowProjectModal(true); }}>Szerkesztés</Button>
              <Button size="sm" onClick={() => { loadUsersAndPartners(); setEditTask(null); setShowTaskModal(true); }}>+ Feladat</Button>
            </div>
          </div>
        </div>

        {/* KPI */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" style={{ marginTop: 20 }}>
          <KpiCard label="Készültség" value={`${pct}%`} sub={`${detail.doneCount}/${detail.taskCount} feladat`} />
          <KpiCard label="Hátralévő nap" value={daysLeft !== null ? String(daysLeft) : '—'}
            sub={daysLeft !== null && daysLeft < 0 ? 'Lejárt!' : formatDate(detail.due_date)}
            variant={daysLeft !== null && daysLeft < 0 ? 'error' : daysLeft !== null && daysLeft <= 7 ? 'warning' : 'default'} />
          <KpiCard label="Feladatok" value={String(detail.taskCount)} sub={`${detail.doneCount} kész`} />
          <KpiCard label="Becsült" value={totalEst ? `${totalEst}h` : '—'} sub="összesen" />
        </div>

        {/* Tabs */}
        <Tabs
          defaultIndex={detailTab}
          tabs={[
            { label: 'Áttekintés', content: <OverviewTab project={detail} members={members} /> },
            {
              label: 'Feladatok', content: (
                <TasksTab tasks={tasks} kanban={kanban} calendar={calendar} milestones={milestones} project={detail}
                  taskView={taskView} setTaskView={setTaskView}
                  calendarMonth={calendarMonth} setCalendarMonth={setCalendarMonth}
                  onDragStart={(id, s) => { dragTaskId.current = id; dragSourceStatus.current = s; }}
                  onDrop={handleDrop}
                  onEditTask={(t) => { loadUsersAndPartners(); setEditTask(t); setShowTaskModal(true); }}
                  onNewTask={() => { loadUsersAndPartners(); setEditTask(null); setShowTaskModal(true); }}
                  onDeleteTask={async (tid) => {
                    try { await del(`/projects/${detail.id}/tasks/${tid}`); toast('Feladat törölve.', 'success'); await loadDetail(detail.id); if (taskView === 'kanban') loadKanban(detail.id); }
                    catch { toast('Törlés sikertelen.', 'error'); }
                  }} />
              ),
            },
            {
              label: 'Mérföldkövek', content: (
                <MilestonesTab milestones={milestones} tasks={tasks}
                  onNew={() => { setEditMilestone(null); setShowMilestoneModal(true); }}
                  onEdit={(m) => { setEditMilestone(m); setShowMilestoneModal(true); }}
                  onDelete={async (mid) => {
                    try { await del(`/projects/${detail.id}/milestones/${mid}`); toast('Törölve.', 'success'); await loadDetail(detail.id); }
                    catch { toast('Törlés sikertelen.', 'error'); }
                  }} />
              ),
            },
            { label: 'Időnaplózás', content: <ProjectTimeTab project={detail} tasks={tasks} /> },
            { label: 'Költségek', content: <ProjectCostTab project={detail} /> },
            { label: 'Dokumentumok', content: <ProjectDocumentsTab project={detail} /> },
          ]}
        />

        {/* Modals */}
        <TaskModal isOpen={showTaskModal} onClose={() => setShowTaskModal(false)}
          task={editTask} projectId={detail.id} users={users} milestones={milestones}
          onSave={async (data) => {
            try {
              if (editTask) { await patch(`/projects/${detail.id}/tasks/${editTask.id}`, data); toast('Feladat frissítve.', 'success'); }
              else { await post(`/projects/${detail.id}/tasks`, data); toast('Feladat létrehozva.', 'success'); }
              setShowTaskModal(false); await loadDetail(detail.id); if (taskView === 'kanban') loadKanban(detail.id);
            } catch (e: any) { toast(e?.message || 'Mentés sikertelen.', 'error'); }
          }} />

        <MilestoneModal isOpen={showMilestoneModal} onClose={() => setShowMilestoneModal(false)}
          milestone={editMilestone} projectId={detail.id}
          onSave={async (data) => {
            try {
              if (editMilestone) { await patch(`/projects/${detail.id}/milestones/${editMilestone.id}`, data); toast('Frissítve.', 'success'); }
              else { await post(`/projects/${detail.id}/milestones`, data); toast('Létrehozva.', 'success'); }
              setShowMilestoneModal(false); await loadDetail(detail.id);
            } catch (e: any) { toast(e?.message || 'Mentés sikertelen.', 'error'); }
          }} />

        <ProjectModal isOpen={showProjectModal} onClose={() => setShowProjectModal(false)}
          project={editProject} users={users}
          onSave={async (data) => {
            try {
              if (editProject) { await patch(`/projects/${editProject.id}`, data); toast('Frissítve.', 'success'); await loadDetail(editProject.id); }
              else { const c = await post<ProjectDetail>('/projects', data); toast('Projekt létrehozva.', 'success'); loadDetail(c.id); }
              setShowProjectModal(false); loadProjects();
            } catch (e: any) { toast(e?.message || 'Mentés sikertelen.', 'error'); }
          }} />
      </div>
    );
  }

  // ── List View ──
  return (
    <div>
      <PageHeader title="Projektek" description={`${projects.length} projekt`}
        actions={
          <div className="flex items-center gap-2">
            <div className="chips hidden sm:flex">
              <button onClick={() => setView('list')} className={`chip${view === 'list' ? ' active' : ''}`}>Lista</button>
              <button onClick={() => setView('cards')} className={`chip${view === 'cards' ? ' active' : ''}`}>Kártyák</button>
            </div>
            <Button onClick={() => { loadUsersAndPartners(); setEditProject(null); setShowProjectModal(true); }}>+ Új projekt</Button>
          </div>
        } />

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Input id="proj-search" placeholder="Keresés..." value={search} onChange={e => setSearch(e.target.value)} className="flex-1 min-w-[160px] max-w-xs" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="form-input text-sm">
          <option value="">Minden státusz</option>
          <option value="planning">Tervezés</option><option value="active">Aktív</option>
          <option value="on_hold">Szünetel</option><option value="completed">Befejezve</option>
          <option value="cancelled">Törölve</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400">Betöltés...</div>
      ) : projects.length === 0 ? (
        <EmptyState title="Nincs projekt" description="Hozd létre az első projektet!"
          action={<Button onClick={() => { loadUsersAndPartners(); setEditProject(null); setShowProjectModal(true); }}>+ Új projekt</Button>} />
      ) : view === 'list' ? (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Projekt</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase hidden md:table-cell">Partner</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase hidden lg:table-cell">Felelős</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase hidden lg:table-cell">Határidő</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase w-28">Haladás</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Státusz</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {projects.map(p => {
                  const pct = progressPercent(Number(p.done_count), Number(p.task_count));
                  return (
                    <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" onClick={() => loadDetail(p.id)}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#1a2540]">{p.name}</div>
                        <div className="text-xs text-gray-400">{p.project_code}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{p.partner_name || '—'}</td>
                      <td className="px-4 py-3 text-gray-500 hidden lg:table-cell">{p.owner_name || '—'}</td>
                      <td className={`px-4 py-3 text-xs hidden lg:table-cell ${deadlineColor(p.due_date)}`}>{formatDate(p.due_date)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-[#F47B20] rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-gray-400 w-7 text-right">{pct}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3"><Badge variant={STATUS_MAP[p.status]?.variant || 'default'}>{STATUS_MAP[p.status]?.label || p.status}</Badge></td>
                      <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" onClick={() => loadDetail(p.id)}>Megnyitás</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(p => {
            const pct = progressPercent(Number(p.done_count), Number(p.task_count));
            const days = daysRemaining(p.due_date);
            return (
              <Card key={p.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => loadDetail(p.id)}>
                <Card.Body>
                  <div className="flex items-start justify-between mb-3">
                    <div><h3 className="font-semibold text-[#1a2540]">{p.name}</h3><p className="text-xs text-gray-400">{p.project_code}</p></div>
                    <Badge variant={STATUS_MAP[p.status]?.variant || 'default'}>{STATUS_MAP[p.status]?.label || p.status}</Badge>
                  </div>
                  {p.partner_name && <p className="text-sm text-gray-500 mb-3">{p.partner_name}</p>}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Készültség</span><span>{pct}%</span></div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#F47B20] rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>{p.owner_name || '—'}</span>
                    <span>{days !== null ? (days < 0 ? `${Math.abs(days)} napja lejárt` : `${days} nap`) : '—'}</span>
                  </div>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}

      <ProjectModal isOpen={showProjectModal} onClose={() => setShowProjectModal(false)}
        project={editProject} users={users}
        onSave={async (data) => {
          try {
            if (editProject) { await patch(`/projects/${editProject.id}`, data); toast('Frissítve.', 'success'); await loadDetail(editProject.id); }
            else { const c = await post<ProjectDetail>('/projects', data); toast('Projekt létrehozva.', 'success'); loadDetail(c.id); }
            setShowProjectModal(false); loadProjects();
          } catch (e: any) { toast(e?.message || 'Mentés sikertelen.', 'error'); }
        }} />
    </div>
  );
}

// ── KpiCard ──

function KpiCard({ label, value, sub, variant = 'default' }: { label: string; value: string; sub: string; variant?: 'default' | 'warning' | 'error' }) {
  const colors = { default: 'text-[#1a2540]', warning: 'text-amber-600', error: 'text-red-600' };
  return (
    <Card><Card.Body className="text-center py-4">
      <div className="text-xs font-medium text-gray-500 mb-1.5">{label}</div>
      <div className={`text-2xl sm:text-3xl font-bold ${colors[variant]}`}>{value}</div>
      <div className="text-xs text-gray-400 mt-1 truncate">{sub}</div>
    </Card.Body></Card>
  );
}

// ── OverviewTab ──

function OverviewTab({ project, members }: { project: ProjectDetail; members: Member[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
      <Card>
        <Card.Header><h3 className="font-semibold">Alapadatok</h3></Card.Header>
        <Card.Body className="space-y-3 text-sm">
          <Row l="Kód" v={project.project_code} />
          <Row l="Partner" v={project.partner_name || 'Belső'} />
          <Row l="Felelős" v={project.owner_name || '—'} />
          <Row l="Kezdés" v={formatDate(project.start_date)} />
          <Row l="Határidő" v={formatDate(project.due_date)} />
          <Row l="ÁFA" v={`${project.vat_rate}%`} />
          {project.hourly_rate && <Row l="Alapóradíj" v={`${project.hourly_rate.toLocaleString('hu-HU')} Ft/óra`} />}
          <div className="flex justify-between items-center gap-4">
            <span className="text-gray-500 shrink-0">Státusz</span>
            <Badge variant={STATUS_MAP[project.status]?.variant || 'default'}>{STATUS_MAP[project.status]?.label || project.status}</Badge>
          </div>
          {project.is_billable && (
            <div className="flex justify-between gap-4">
              <span className="text-gray-500 shrink-0">Számlázható</span>
              <span className="text-green-600 font-medium">Igen</span>
            </div>
          )}
          {project.description && (
            <div className="pt-3 mt-1 border-t border-gray-100">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Leírás</div>
              <div className="text-sm text-gray-600 leading-relaxed">{project.description}</div>
            </div>
          )}
        </Card.Body>
      </Card>
      <Card>
        <Card.Header><h3 className="font-semibold">Csapat ({members.length})</h3></Card.Header>
        <Card.Body>
          {members.length === 0 ? <p className="text-sm text-gray-400">Nincs tag.</p> : (
            <div className="space-y-3">
              {members.map(m => (
                <div key={m.id} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#1a2540] text-white flex items-center justify-center text-xs font-bold shrink-0">
                      {m.user_name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div>
                      <div className="text-sm font-medium">{m.user_name}</div>
                      <div className="text-xs text-gray-400">{m.role === 'owner' ? 'Felelős' : m.role === 'manager' ? 'Vezető' : 'Tag'}</div>
                    </div>
                  </div>
                  {m.hourly_rate && <span className="text-xs text-gray-500 shrink-0">{m.hourly_rate.toLocaleString('hu-HU')} Ft/h</span>}
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
function Row({ l, v }: { l: string; v: string }) {
  return <div className="flex justify-between gap-4"><span className="text-gray-500 shrink-0">{l}</span><span className="font-medium text-right">{v}</span></div>;
}

// ── TasksTab ──

function TasksTab({ tasks, kanban, calendar, milestones, project, taskView, setTaskView, calendarMonth, setCalendarMonth, onDragStart, onDrop, onEditTask, onNewTask, onDeleteTask }: {
  tasks: Task[]; kanban: KanbanColumn[]; calendar: CalendarEvent[]; milestones: Milestone[]; project: ProjectDetail;
  taskView: string; setTaskView: (v: 'kanban' | 'list' | 'gantt' | 'calendar') => void;
  calendarMonth: string; setCalendarMonth: (m: string) => void;
  onDragStart: (id: string, s: string) => void; onDrop: (s: string) => void;
  onEditTask: (t: Task) => void; onNewTask: () => void; onDeleteTask: (id: string) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="chips">
          {(['kanban', 'list', 'gantt', 'calendar'] as const).map(v => (
            <button key={v} onClick={() => setTaskView(v)} className={`chip${taskView === v ? ' active' : ''}`}>
              {v === 'kanban' ? 'Kanban' : v === 'list' ? 'Lista' : v === 'gantt' ? 'Gantt' : 'Naptár'}
            </button>
          ))}
        </div>
        <Button size="sm" onClick={onNewTask}>+ Feladat</Button>
      </div>
      {taskView === 'kanban' ? <KanbanBoard columns={kanban} onDragStart={onDragStart} onDrop={onDrop} onEditTask={onEditTask} />
        : taskView === 'list' ? <TaskListView tasks={tasks} onEditTask={onEditTask} onDeleteTask={onDeleteTask} />
        : taskView === 'gantt' ? <GanttView tasks={tasks} milestones={milestones} project={project} />
        : <CalendarView events={calendar} month={calendarMonth} setMonth={setCalendarMonth} />}
    </div>
  );
}

// ── KanbanBoard ──

function KanbanBoard({ columns, onDragStart, onDrop, onEditTask }: {
  columns: KanbanColumn[]; onDragStart: (id: string, s: string) => void; onDrop: (s: string) => void; onEditTask: (t: Task) => void;
}) {
  if (columns.length === 0) return <div className="text-center py-8 text-gray-400 text-sm">Kanban betöltése...</div>;
  return (
    <div className="overflow-x-auto pb-3" style={{ WebkitOverflowScrolling: 'touch' }}>
      <div className="flex gap-4" style={{ minWidth: `${columns.length * 256}px` }}>
        {columns.map(col => (
          <div key={col.status} className="bg-gray-50 rounded-xl p-4 min-h-[200px] flex-1"
            style={{ minWidth: 240 }}
            onDragOver={e => e.preventDefault()} onDrop={() => onDrop(col.status)}>
            <div className="flex items-center justify-between mb-4 px-1">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider truncate mr-2">{col.title}</h4>
              <span className="text-xs font-semibold text-gray-400 bg-gray-200 rounded-full px-2 py-0.5 shrink-0">{col.count}</span>
            </div>
            <div className="space-y-2.5">
              {col.tasks.map(t => (
                <div key={t.id} draggable onDragStart={() => onDragStart(t.id, col.status)}
                  onClick={() => onEditTask(t)}
                  className="bg-white rounded-lg border border-gray-200 p-3 cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow">
                  <div className="text-sm font-medium text-[#1a2540] mb-2.5 leading-snug">{t.title}</div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {t.assignee_name && <span className="w-5 h-5 rounded-full bg-[#F47B20] text-white flex items-center justify-center text-[10px] font-bold shrink-0">{t.assignee_name.charAt(0)}</span>}
                      {t.estimated_hours && <span className="text-[10px] text-gray-400">{t.estimated_hours}h</span>}
                    </div>
                    {t.due_date && (
                      <span className={`text-[10px] font-medium shrink-0 ${deadlineColor(t.due_date)}`}>
                        {new Date(t.due_date).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                  </div>
                  {t.priority !== 'medium' && <div className="mt-2"><span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: PRIORITY_MAP[t.priority]?.color }} /></div>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── TaskListView ──

function TaskListView({ tasks, onEditTask, onDeleteTask }: { tasks: Task[]; onEditTask: (t: Task) => void; onDeleteTask: (id: string) => void }) {
  if (tasks.length === 0) return <EmptyState title="Nincs feladat" description="Hozd létre az első feladatot!" />;
  const sorted = [...tasks].sort((a, b) => {
    if (a.due_date && b.due_date) return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    if (a.due_date) return -1; if (b.due_date) return 1; return a.position - b.position;
  });
  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[500px]">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Cím</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase hidden sm:table-cell">Felelős</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Státusz</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase hidden md:table-cell">Határidő</th>
              <th className="text-right px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {sorted.map(t => (
              <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-4 py-3 cursor-pointer" onClick={() => onEditTask(t)}>
                  <div className="font-medium text-[#1a2540]">{t.title}</div>
                  {t.priority !== 'medium' && <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: PRIORITY_MAP[t.priority]?.color }} />}
                </td>
                <td className="px-4 py-3 text-gray-500 hidden sm:table-cell">{t.assignee_name || '—'}</td>
                <td className="px-4 py-3"><Badge variant={TASK_STATUS_MAP[t.status]?.variant || 'default'}>{TASK_STATUS_MAP[t.status]?.label || t.status}</Badge></td>
                <td className={`px-4 py-3 text-xs hidden md:table-cell ${deadlineColor(t.due_date)}`}>{formatDate(t.due_date)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => onEditTask(t)} className="text-xs text-gray-500 hover:text-[#1a2540] px-2 py-1 rounded hover:bg-gray-100 mr-1 transition-colors">Szerkesztés</button>
                  <button onClick={() => onDeleteTask(t.id)} className="text-xs text-red-400 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50 transition-colors">Törlés</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── GanttView ──

function GanttView({ tasks, milestones, project }: { tasks: Task[]; milestones: Milestone[]; project: ProjectDetail }) {
  const DAY_W = 36;

  const allDates = [
    project.start_date, project.due_date,
    ...tasks.map(t => t.start_date), ...tasks.map(t => t.due_date),
    ...milestones.map(m => m.due_date),
  ].filter(Boolean).map(d => new Date(d!));

  const now = new Date();
  if (allDates.length === 0) {
    allDates.push(new Date(now.getFullYear(), now.getMonth(), 1));
    allDates.push(new Date(now.getFullYear(), now.getMonth() + 2, 0));
  }

  const rawMin = new Date(Math.min(...allDates.map(d => d.getTime())));
  const rawMax = new Date(Math.max(...allDates.map(d => d.getTime())));

  const start = new Date(rawMin); start.setDate(start.getDate() - ((start.getDay() + 6) % 7)); start.setHours(0, 0, 0, 0);
  const end = new Date(rawMax); end.setDate(end.getDate() + 14);

  const totalDays = Math.ceil((end.getTime() - start.getTime()) / 86400000);

  const weeks: Date[] = [];
  const wd = new Date(start);
  while (wd < end) { weeks.push(new Date(wd)); wd.setDate(wd.getDate() + 7); }

  const offset = (d: Date) => Math.max(0, Math.floor((new Date(d).setHours(0,0,0,0) - start.getTime()) / 86400000));
  const todayOff = offset(now);

  const tasksByMs: Record<string, Task[]> = { '': [] };
  milestones.forEach(m => { tasksByMs[m.id] = []; });
  tasks.forEach(t => { const k = t.milestone_id || ''; if (!tasksByMs[k]) tasksByMs[k] = []; tasksByMs[k].push(t); });

  const COL_W = 160;

  if (tasks.length === 0 && milestones.length === 0) {
    return <EmptyState title="Nincs megjeleníthető adat" description="Adj hozzá feladatokat határidőkkel a Gantt nézethez." />;
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200">
      <div style={{ minWidth: `${COL_W + totalDays * DAY_W}px` }}>
        {/* Week header */}
        <div className="flex border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
          <div style={{ width: COL_W, minWidth: COL_W }} className="shrink-0 border-r border-gray-200 px-3 py-2 text-xs font-semibold text-gray-500">Feladat</div>
          <div className="flex-1 flex">
            {weeks.map((w, i) => (
              <div key={i} style={{ width: `${7 * DAY_W}px`, minWidth: `${7 * DAY_W}px` }}
                className="border-r border-gray-100 px-2 py-2 text-xs text-gray-400">
                {w.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}
              </div>
            ))}
          </div>
        </div>

        {/* Today line wrapper */}
        <div className="relative">
          <div className="absolute top-0 bottom-0 z-10 pointer-events-none"
            style={{ left: `${COL_W + todayOff * DAY_W}px`, borderLeft: '2px dashed #EF4444' }} />

          {/* Milestones + tasks */}
          {milestones.map(m => (
            <div key={m.id}>
              <div className="flex items-center border-b border-gray-100 bg-blue-50/60">
                <div style={{ width: COL_W, minWidth: COL_W }} className="shrink-0 border-r border-gray-200 px-3 py-2 text-xs font-bold text-blue-700 flex items-center gap-1.5">
                  <span className="text-blue-400">◆</span> {m.name}
                </div>
                <div className="flex-1 relative h-8">
                  {m.due_date && (
                    <div className="absolute top-1 w-6 h-6 rotate-45 bg-blue-500 rounded-sm border-2 border-white shadow"
                      style={{ left: `${offset(new Date(m.due_date)) * DAY_W - 12}px` }}
                      title={formatDate(m.due_date)} />
                  )}
                </div>
              </div>
              {(tasksByMs[m.id] || []).map(t => <GanttRow key={t.id} task={t} offset={offset} DAY_W={DAY_W} COL_W={COL_W} />)}
            </div>
          ))}

          {tasksByMs[''].length > 0 && (
            <div>
              {milestones.length > 0 && (
                <div className="flex border-b border-gray-100 bg-gray-50/60">
                  <div style={{ width: COL_W, minWidth: COL_W }} className="shrink-0 border-r border-gray-200 px-3 py-2 text-xs font-semibold text-gray-500">Egyéb feladatok</div>
                  <div className="flex-1 h-8" />
                </div>
              )}
              {tasksByMs[''].map(t => <GanttRow key={t.id} task={t} offset={offset} DAY_W={DAY_W} COL_W={COL_W} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GanttRow({ task, offset, DAY_W, COL_W }: { task: Task; offset: (d: Date) => number; DAY_W: number; COL_W: number }) {
  const s = task.start_date ? offset(new Date(task.start_date)) : (task.due_date ? offset(new Date(task.due_date)) - 1 : null);
  const e = task.due_date ? offset(new Date(task.due_date)) : null;
  const w = s !== null && e !== null ? Math.max(1, e - s + 1) : 1;
  return (
    <div className="flex items-center border-b border-gray-50 hover:bg-gray-50">
      <div style={{ width: COL_W, minWidth: COL_W }} className="shrink-0 border-r border-gray-100 px-3 py-2 flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: PRIORITY_MAP[task.priority]?.color || '#999' }} />
        <span className="text-xs text-[#1a2540] truncate">{task.title}</span>
      </div>
      <div className="flex-1 relative h-8">
        {s !== null && (
          <div className="absolute top-1.5 h-5 rounded-md flex items-center px-1.5"
            style={{ left: `${s * DAY_W}px`, width: `${w * DAY_W - 3}px`, backgroundColor: GANTT_COLORS[task.status] || '#CBD5E1' }}
            title={`${task.title}: ${task.start_date || '?'} → ${task.due_date || '?'}`}>
            {w > 3 && <span className="text-[10px] text-gray-700 truncate font-medium">{task.title}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── CalendarView ──

function CalendarView({ events, month, setMonth }: { events: CalendarEvent[]; month: string; setMonth: (m: string) => void }) {
  const [y, mon] = month.split('-').map(Number);
  const daysInMonth = new Date(y, mon, 0).getDate();
  const firstDow = (new Date(y, mon - 1, 1).getDay() + 6) % 7;
  const byDay: Record<number, CalendarEvent[]> = {};
  events.forEach(e => { const d = new Date(e.date).getDate(); if (!byDay[d]) byDay[d] = []; byDay[d].push(e); });

  const prevM = () => { const d = new Date(y, mon - 2, 1); setMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); };
  const nextM = () => { const d = new Date(y, mon, 1); setMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); };

  const todayY = new Date().getFullYear();
  const todayMon = new Date().getMonth() + 1;
  const todayD = new Date().getDate();
  const isCurrentMonth = y === todayY && mon === todayMon;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Button size="sm" variant="ghost" onClick={prevM}>← Előző</Button>
        <h3 className="font-semibold text-sm sm:text-base">
          {y}. {new Date(y, mon - 1).toLocaleDateString('hu-HU', { month: 'long' })}
        </h3>
        <Button size="sm" variant="ghost" onClick={nextM}>Következő →</Button>
      </div>

      <div className="rounded-xl border border-gray-200 overflow-hidden">
        {/* Hét fejléc */}
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
          {['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'].map((d, i) => (
            <div key={d} className={`text-center text-xs font-semibold py-2.5 ${i >= 5 ? 'text-gray-400' : 'text-gray-500'}`}>{d}</div>
          ))}
        </div>

        {/* Napok */}
        <div className="grid grid-cols-7 divide-x divide-y divide-gray-100">
          {Array.from({ length: firstDow }).map((_, i) => (
            <div key={`e${i}`} className="min-h-[80px] sm:min-h-[100px] bg-gray-50/50" />
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dayEvs = byDay[day] || [];
            const isToday = isCurrentMonth && day === todayD;
            const dow = (firstDow + i) % 7; // 0=H, 5=Szo, 6=V
            const isWeekend = dow >= 5;
            return (
              <div key={day} className={`min-h-[80px] sm:min-h-[100px] p-2 ${isWeekend ? 'bg-gray-50/60' : 'bg-white'}`}>
                <div className={`text-xs font-semibold mb-1.5 w-6 h-6 flex items-center justify-center rounded-full ${
                  isToday ? 'bg-[#1a2540] text-white' : isWeekend ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {day}
                </div>
                <div className="space-y-1">
                  {dayEvs.slice(0, 2).map(ev => (
                    <div key={ev.taskId} className={`text-xs px-1.5 py-0.5 rounded truncate leading-snug ${
                      ev.isOverdue ? 'bg-red-50 text-red-700' : ev.status === 'done' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'
                    }`}>
                      {ev.title}
                    </div>
                  ))}
                  {dayEvs.length > 2 && (
                    <div className="text-[11px] text-gray-400 px-1">+{dayEvs.length - 2} további</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── MilestonesTab ──

function MilestonesTab({ milestones, tasks, onNew, onEdit, onDelete }: {
  milestones: Milestone[]; tasks: Task[]; onNew: () => void; onEdit: (m: Milestone) => void; onDelete: (id: string) => void;
}) {
  return (
    <div>
      <div className="flex justify-end mb-4"><Button size="sm" onClick={onNew}>+ Új mérföldkő</Button></div>
      {milestones.length === 0 ? <EmptyState title="Nincs mérföldkő" description="Hozz létre mérföldköveket!" action={<Button onClick={onNew}>+ Új mérföldkő</Button>} /> : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[400px]">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Név</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Határidő</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase w-36">Haladás</th>
                  <th className="text-right px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {milestones.map(m => {
                  const mT = tasks.filter(t => t.milestone_id === m.id);
                  const pct = mT.length > 0 ? Math.round((mT.filter(t => t.status === 'done').length / mT.length) * 100) : 0;
                  return (
                    <tr key={m.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{m.name}</td>
                      <td className={`px-4 py-3 text-xs ${deadlineColor(m.due_date)}`}>{formatDate(m.due_date)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-gray-100 rounded-full"><div className="h-full bg-[#F47B20] rounded-full" style={{ width: `${pct}%` }} /></div>
                          <span className="text-xs text-gray-400">{pct}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => onEdit(m)} className="text-xs text-gray-500 hover:text-[#1a2540] px-2 py-1 rounded hover:bg-gray-100 mr-1 transition-colors">Szerkesztés</button>
                        <button onClick={() => onDelete(m.id)} className="text-xs text-red-400 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50 transition-colors">Törlés</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ── ProjectTimeTab ──

function ProjectTimeTab({ project, tasks }: { project: ProjectDetail; tasks: Task[] }) {
  const { toast } = useToast();
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [activeTimer, setActiveTimer] = useState<ActiveTimer | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showManual, setShowManual] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [manTaskId, setManTaskId] = useState('');
  const [manDate, setManDate] = useState(new Date().toISOString().slice(0, 10));
  const [manH, setManH] = useState('');
  const [manM, setManM] = useState('');
  const [manDesc, setManDesc] = useState('');
  const [manRate, setManRate] = useState('');
  const [manBillable, setManBillable] = useState(true);
  const [manSaving, setManSaving] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<{ startedAt: number; pausedSec: number } | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [er, tr] = await Promise.all([
        get<{ entries: TimeEntry[] }>(`/time-entries?project_id=${project.id}&all_users=true`),
        get<any>('/active-timer'),
      ]);
      setEntries(er.entries || []);
      setActiveTimer(tr?.user_id ? tr : null);
    } catch { /* silent */ }
    setLoading(false);
  }, [project.id]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    if (activeTimer?.project_id === project.id && !activeTimer.paused_at) {
      timerRef.current = { startedAt: new Date(activeTimer.started_at).getTime(), pausedSec: activeTimer.paused_seconds || 0 };
      const tick = () => { if (!timerRef.current) return; setElapsed(Math.max(0, Math.floor((Date.now() - timerRef.current.startedAt - timerRef.current.pausedSec * 1000) / 1000))); };
      tick(); intervalRef.current = setInterval(tick, 1000);
    } else if (activeTimer?.project_id === project.id && activeTimer.paused_at) {
      const ms = Date.now() - new Date(activeTimer.started_at).getTime() - (activeTimer.paused_seconds || 0) * 1000 - (Date.now() - new Date(activeTimer.paused_at).getTime());
      setElapsed(Math.max(0, Math.floor(ms / 1000)));
    } else { setElapsed(0); }
    return () => { if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; } };
  }, [activeTimer, project.id]);

  const doTimer = async (action: string) => {
    try {
      await post(`/active-timer/${action}`, {});
      if (action === 'stop') toast('Bejegyzés rögzítve.', 'success');
      else if (action === 'pause') toast('Szüneteltetés.', 'info');
      else toast('Időzítő folytatva.', 'success');
      await loadData();
    } catch (e: any) { toast(e?.message || 'Hiba.', 'error'); }
  };

  const handleStart = async (taskId: string) => {
    try { await post('/active-timer/start', { projectId: project.id, taskId }); toast('Időzítő elindítva.', 'success'); await loadData(); }
    catch (e: any) { toast(e?.message || 'Hiba.', 'error'); }
  };

  const handleManualSave = async () => {
    const dur = (parseInt(manH || '0') * 60) + parseInt(manM || '0');
    if (dur < 1) { toast('Legalább 1 perc szükséges.', 'warning'); return; }
    setManSaving(true);
    try {
      await post('/time-entries', { projectId: project.id, taskId: manTaskId || null, entryDate: manDate, durationMinutes: dur, description: manDesc || null, isBillable: manBillable, hourlyRate: manRate ? parseInt(manRate) : undefined });
      toast('Bejegyzés rögzítve.', 'success');
      setManH(''); setManM(''); setManDesc(''); setManTaskId(''); setManRate(''); setShowManual(false);
      await loadData();
    } catch (e: any) { toast(e?.message || 'Hiba.', 'error'); }
    setManSaving(false);
  };

  const handleDelete = async (id: string) => {
    try { await del(`/time-entries/${id}`); toast('Törölve.', 'success'); await loadData(); }
    catch (e: any) { toast(e?.message || 'Hiba.', 'error'); }
  };

  const vatRate = project.vat_rate ?? 27;
  const activeForProject = activeTimer?.project_id === project.id;
  const activeTasks = tasks.filter(t => t.status !== 'done' && t.status !== 'cancelled');

  const byUser = useMemo(() => {
    const map: Record<string, {
      name: string;
      totalMin: number;
      net: number;
      byTask: Record<string, { taskName: string; entries: TimeEntry[]; totalMin: number }>;
    }> = {};
    entries.forEach(e => {
      if (!map[e.user_id]) map[e.user_id] = { name: e.user_name, totalMin: 0, net: 0, byTask: {} };
      const taskKey = e.task_id || '__no_task__';
      const taskLabel = tasks.find(t => t.id === e.task_id)?.title || e.description || 'Feladat nélkül';
      if (!map[e.user_id].byTask[taskKey]) map[e.user_id].byTask[taskKey] = { taskName: taskLabel, entries: [], totalMin: 0 };
      map[e.user_id].byTask[taskKey].entries.push(e);
      map[e.user_id].byTask[taskKey].totalMin += e.duration_minutes;
      map[e.user_id].totalMin += e.duration_minutes;
      if (e.is_billable && e.hourly_rate) map[e.user_id].net += Math.round((e.duration_minutes / 60) * e.hourly_rate);
    });
    return map;
  }, [entries, tasks]);

  const totalNet = useMemo(() => Object.values(byUser).reduce((s, u) => s + u.net, 0), [byUser]);
  const totalGross = Math.round(totalNet * (1 + vatRate / 100));
  const totalMin = entries.reduce((s, e) => s + e.duration_minutes, 0);

  if (loading) return <div className="py-12 text-center text-gray-400">Betöltés...</div>;

  return (
    <div className="space-y-4 sm:space-y-6">

      {/* Active Timer */}
      {activeForProject && activeTimer && (
        <div className={`rounded-xl border-2 p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${activeTimer.paused_at ? 'border-amber-300 bg-amber-50' : 'border-green-300 bg-green-50'}`}>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">{activeTimer.paused_at ? '⏸ Szüneteltetve' : '● Fut'}</div>
            <div className="font-semibold text-[#1a2540]">{tasks.find(t => t.id === activeTimer.task_id)?.title || 'Feladat nélkül'}</div>
            <div className={`text-3xl sm:text-4xl font-mono font-bold tabular-nums mt-1 ${activeTimer.paused_at ? 'text-amber-600' : 'text-green-600'}`}>{fmtElapsed(elapsed)}</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {activeTimer.paused_at
              ? <Button variant="success" onClick={() => doTimer('resume')}>▶ Folytatás</Button>
              : <Button onClick={() => doTimer('pause')} style={{ background: '#f59e0b' }}>⏸ Szünet</Button>}
            <Button variant="secondary" onClick={() => doTimer('stop')}>⏹ Befejezés &amp; Rögzítés</Button>
          </div>
        </div>
      )}

      {/* Header buttons */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="ghost" onClick={() => setShowManual(v => !v)}>
            {showManual ? '✕ Bezárás' : '+ Manuális rögzítés'}
          </Button>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="ghost" onClick={() => setShowShare(true)}>
            🔗 Megosztás
          </Button>
          {entries.some(e => e.is_billable && !e.is_billed) && (
            <Button size="sm" onClick={() => setShowInvoice(true)}>
              📄 Számla készítése
            </Button>
          )}
        </div>
      </div>

      {/* Manual entry */}
      {showManual && (
        <Card>
          <Card.Header><h3 className="font-semibold text-sm">Manuális rögzítés</h3></Card.Header>
          <Card.Body className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Feladat</label>
                <select value={manTaskId} onChange={e => setManTaskId(e.target.value)} className="form-input text-sm">
                  <option value="">Feladat nélkül</option>
                  {tasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Dátum *</label>
                <input type="date" value={manDate} onChange={e => setManDate(e.target.value)} max={new Date().toISOString().slice(0,10)} className="form-input text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Óra</label>
                <input type="number" value={manH} onChange={e => setManH(e.target.value)} placeholder="0" min="0" max="23" className="form-input text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Perc</label>
                <input type="number" value={manM} onChange={e => setManM(e.target.value)} placeholder="0" min="0" max="59" className="form-input text-sm" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Leírás</label>
                <input type="text" value={manDesc} onChange={e => setManDesc(e.target.value)} placeholder="Elvégzett munka..." className="form-input text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Egyedi óradíj (Ft)</label>
                <input type="number" value={manRate} onChange={e => setManRate(e.target.value)} placeholder="feladat/projekt alapján" className="form-input text-sm" />
              </div>
            </div>
            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={manBillable} onChange={e => setManBillable(e.target.checked)} />
                Számlázható
              </label>
              <button onClick={handleManualSave} disabled={manSaving}
                className="px-4 py-2 rounded-lg bg-[#F47B20] text-white text-sm font-semibold hover:bg-[#d96a15] transition-colors disabled:opacity-50">
                {manSaving ? 'Mentés...' : 'Rögzítés'}
              </button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Tasks */}
      <Card>
        <Card.Header>
          <h3 className="font-semibold">Feladatok — időzítő</h3>
        </Card.Header>
        {activeTasks.length === 0 ? (
          <Card.Body><p className="text-sm text-gray-400">Nincs aktív feladat.</p></Card.Body>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[320px]">
              <colgroup>
                <col />
                <col className="hidden sm:table-column" style={{ width: '120px' }} />
                <col className="hidden md:table-column" style={{ width: '110px' }} />
                <col style={{ width: '120px' }} />
              </colgroup>
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-500 text-xs uppercase">Feladat</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-500 text-xs uppercase hidden sm:table-cell">Felelős</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-500 text-xs uppercase hidden md:table-cell">Óradíj</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-gray-500 text-xs uppercase">Időzítő</th>
                </tr>
              </thead>
              <tbody>
                {activeTasks.map(t => {
                  const isRunning = activeForProject && activeTimer?.task_id === t.id && !activeTimer?.paused_at;
                  const isPaused = activeForProject && activeTimer?.task_id === t.id && !!activeTimer?.paused_at;
                  const hasOther = activeForProject && activeTimer?.task_id !== t.id;
                  return (
                    <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-[#1a2540]">{t.title}</td>
                      <td className="px-4 py-3 text-gray-500 hidden sm:table-cell">{t.assignee_name || '—'}</td>
                      <td className="px-4 py-3 text-gray-600 hidden md:table-cell">
                        {t.hourly_rate ? <span className="font-medium">{t.hourly_rate.toLocaleString('hu-HU')} <span className="text-gray-400 font-normal text-xs">Ft/h</span></span> : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isRunning ? (
                          <span className="inline-flex items-center gap-1.5 text-green-600 text-xs font-semibold">
                            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />Fut — {fmtElapsed(elapsed)}
                          </span>
                        ) : isPaused ? (
                          <span className="text-amber-600 text-xs font-semibold">⏸ Szünet</span>
                        ) : (
                          <Button size="sm" variant="secondary" onClick={() => handleStart(t.id)} disabled={!!hasOther}>
                            ▶ Indítás
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Time entries by user */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-semibold">Időnaplók</h3>
            {totalMin > 0 && <span className="text-sm text-gray-500">Összesen: <strong>{fmtDur(totalMin)}</strong>{totalNet > 0 ? ` · ${totalNet.toLocaleString('hu-HU')} Ft nettó` : ''}</span>}
          </div>
        </Card.Header>
        {entries.length === 0 ? (
          <Card.Body><p className="text-sm text-gray-400 text-center py-4">Még nincs bejegyzés. Indíts el egy időzítőt vagy add meg manuálisan.</p></Card.Body>
        ) : (
          <div className="divide-y divide-gray-100">
            {Object.entries(byUser).map(([uid, ud]) => (
              <div key={uid}>
                {/* Felhasználó fejléc */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50/80">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-[#1a2540] text-white flex items-center justify-center text-sm font-bold shrink-0">{ud.name?.charAt(0).toUpperCase() || '?'}</div>
                    <div>
                      <div className="font-semibold text-sm">{ud.name}</div>
                      <div className="text-xs text-gray-400">{fmtDur(ud.totalMin)}{ud.net > 0 ? ` · ${ud.net.toLocaleString('hu-HU')} Ft` : ''}</div>
                    </div>
                  </div>
                </div>
                {/* Feladat-szintű csoportok */}
                {Object.entries(ud.byTask).map(([taskKey, tg]) => (
                  <div key={taskKey}>
                    {/* Feladat alcím */}
                    <div className="flex items-center gap-2 px-4 py-1.5 bg-blue-50/40 border-b border-blue-100/60">
                      <span className="text-[11px] font-semibold text-blue-600 uppercase tracking-wide truncate">{tg.taskName}</span>
                      <span className="text-[11px] text-blue-400 shrink-0">{fmtDur(tg.totalMin)}</span>
                    </div>
                    {/* Bejegyzések */}
                    {tg.entries.map(e => {
                      const net = e.is_billable && e.hourly_rate ? Math.round((e.duration_minutes / 60) * e.hourly_rate) : null;
                      return (
                        <div key={e.id} className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-50 last:border-0 hover:bg-gray-50 group text-sm">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-[#1a2540] truncate">{e.description || new Date(e.entry_date).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}</span>
                              {e.is_billed && <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded shrink-0">Számlázva</span>}
                            </div>
                          </div>
                          <span className="text-gray-400 text-xs shrink-0 hidden sm:block">{new Date(e.entry_date).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}</span>
                          <span className="font-medium text-gray-700 shrink-0">{fmtDur(e.duration_minutes)}</span>
                          {e.hourly_rate ? <span className="text-xs text-gray-400 shrink-0 hidden md:block">{e.hourly_rate.toLocaleString('hu-HU')} Ft/h</span> : <span className="hidden md:block w-16" />}
                          <span className="font-semibold text-[#1a2540] shrink-0 text-right w-20">{net ? `${net.toLocaleString('hu-HU')} Ft` : '—'}</span>
                          {!e.is_billed && (
                            <button onClick={() => handleDelete(e.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600 text-xs px-1 shrink-0">✕</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Financial summary */}
      {totalNet > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="rounded-xl border border-gray-200 p-4 text-center">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Nettó összeg</div>
            <div className="text-xl sm:text-2xl font-bold text-[#1a2540]">{totalNet.toLocaleString('hu-HU')} Ft</div>
            <div className="text-xs text-gray-400 mt-0.5">{vatRate}% ÁFA nélkül</div>
          </div>
          <div className="rounded-xl border border-gray-200 p-4 text-center">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">ÁFA ({vatRate}%)</div>
            <div className="text-xl sm:text-2xl font-bold text-[#1a2540]">{(totalGross - totalNet).toLocaleString('hu-HU')} Ft</div>
            <div className="text-xs text-gray-400 mt-0.5">hozzáadottérték-adó</div>
          </div>
          <div className="rounded-xl border-2 border-[#F47B20] bg-orange-50 p-4 text-center">
            <div className="text-xs font-semibold text-[#F47B20] uppercase tracking-wide mb-1">Bruttó végösszeg</div>
            <div className="text-xl sm:text-2xl font-bold text-[#F47B20]">{totalGross.toLocaleString('hu-HU')} Ft</div>
            <div className="text-xs text-gray-500 mt-0.5">számlázható összeg</div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      <ShareModal isOpen={showShare} onClose={() => setShowShare(false)} projectId={project.id} />

      {/* Invoice Modal */}
      {showInvoice && (
        <InvoiceFromTimeModal
          isOpen={showInvoice} onClose={() => setShowInvoice(false)}
          projectId={project.id} partnerId={project.partner_id} partnerName={project.partner_name} entries={entries}
          tasks={tasks}
          onSuccess={() => { loadData(); }}
        />
      )}
    </div>
  );
}

// ── ShareModal ──

function ShareModal({ isOpen, onClose, projectId }: { isOpen: boolean; onClose: () => void; projectId: string }) {
  const { toast } = useToast();
  const [shares, setShares] = useState<Share[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [costVisible, setCostVisible] = useState(false);
  const [validUntil, setValidUntil] = useState('');
  const [newLink, setNewLink] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await get<Share[]>(`/projects/${projectId}/shares`); setShares(Array.isArray(d) ? d : []); }
    catch { /* silent */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { if (isOpen) { setNewLink(''); load(); } }, [isOpen, load]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const s = await post<Share>(`/projects/${projectId}/shares`, {
        includeHourlyRate: costVisible, validUntil: validUntil || null, includeDescriptions: true,
      });
      const url = `${window.location.origin}/public/idonaplo/${s.share_token}`;
      setNewLink(url); await load();
    } catch (e: any) { toast(e?.message || 'Hiba a megosztás létrehozásakor.', 'error'); }
    setCreating(false);
  };

  const handleRevoke = async (id: string) => {
    try { await post(`/projects/${projectId}/shares/${id}/revoke`, {}); toast('Megosztás visszavonva.', 'success'); await load(); }
    catch { toast('Hiba.', 'error'); }
  };

  const handleCopy = (link: string) => {
    navigator.clipboard.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Megosztható időnapló-link">
      <div className="space-y-5">
        {/* New share */}
        <div className="bg-blue-50 rounded-xl p-4 space-y-3">
          <div className="text-sm font-semibold text-[#1a2540]">Új megosztás létrehozása</div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={costVisible} onChange={e => setCostVisible(e.target.checked)} />
            <span>Óradíj / összeg is látható legyen <span className="text-gray-400">(alapban csak az idő látszik)</span></span>
          </label>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Lejárat (opcionális)</label>
            <input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} min={new Date().toISOString().slice(0,10)} className="form-input text-sm" />
          </div>
          <div className="text-xs text-amber-600 bg-amber-50 rounded-lg p-2">
            ⚠ A link ismeretében bárki (bejelentkezés nélkül) megtekintheti a naplót. Csak megbízható személyeknek küldd el.
          </div>
          <Button variant="secondary" fullWidth onClick={handleCreate} disabled={creating} loading={creating}>
            {!creating && '🔗 Megosztó link generálása'}
          </Button>

          {newLink && (
            <div className="mt-2">
              <div className="text-xs text-green-600 font-semibold mb-1">Link létrehozva — másold ki és küldd el:</div>
              <div className="flex gap-2">
                <input readOnly value={newLink} className="form-input text-xs flex-1" onClick={e => (e.target as HTMLInputElement).select()} />
                <Button size="sm" variant="success" onClick={() => handleCopy(newLink)}>
                  {copied ? '✓ Másolva' : 'Másolás'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Existing shares */}
        <div>
          <div className="text-sm font-semibold text-[#1a2540] mb-2">Meglévő megosztások</div>
          {loading ? <div className="text-sm text-gray-400">Betöltés...</div> : shares.length === 0 ? (
            <p className="text-sm text-gray-400">Még nincs aktív megosztás.</p>
          ) : (
            <div className="space-y-2">
              {shares.map(s => {
                const link = `${window.location.origin}/public/idonaplo/${s.share_token}`;
                return (
                  <div key={s.id} className={`rounded-lg border p-3 ${s.is_revoked ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-gray-200'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-xs text-gray-600 truncate font-mono">{s.share_token.slice(0, 20)}...</div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {s.include_hourly_rate && <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">Összeg látszik</span>}
                          {s.valid_until && <span className="text-xs text-gray-400">Lejár: {new Date(s.valid_until).toLocaleDateString('hu-HU')}</span>}
                          <span className="text-xs text-gray-400">{s.view_count} megtekintés</span>
                          {s.is_revoked && <span className="text-xs bg-red-50 text-red-500 px-1.5 py-0.5 rounded">Visszavonva</span>}
                        </div>
                      </div>
                      {!s.is_revoked && (
                        <div className="flex gap-1.5 shrink-0">
                          <Button size="sm" variant="ghost" onClick={() => handleCopy(link)}>Másolás</Button>
                          <Button size="sm" variant="danger" onClick={() => handleRevoke(s.id)}>Visszavon</Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end"><Button variant="ghost" onClick={onClose}>Bezárás</Button></div>
      </div>
    </Modal>
  );
}

// ── InvoiceFromTimeModal ──

const PM_OPTIONS = [
  { value: 'transfer', label: 'Átutalás',    icon: '🏦' },
  { value: 'cash',     label: 'Készpénz',    icon: '💵' },
  { value: 'card',     label: 'Bankkártya',  icon: '💳' },
  { value: 'other',    label: 'Egyéb',       icon: '📋' },
];

function InvoiceFromTimeModal({ isOpen, onClose, projectId, partnerId, partnerName, entries, tasks, onSuccess }: {
  isOpen: boolean; onClose: () => void; projectId: string; partnerId: string; partnerName: string;
  entries: TimeEntry[]; tasks: Task[]; onSuccess: () => void;
}) {
  const { toast } = useToast();
  const unbilled = entries.filter(e => e.is_billable && !e.is_billed);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<{ lines: any[]; totals: any } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [step, setStep] = useState<'select' | 'confirm'>('select');
  const [paymentMethod, setPaymentMethod] = useState('transfer');
  const [creating, setCreating] = useState(false);
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDateState] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 15); return d.toISOString().slice(0, 10);
  });
  const [postIssue, setPostIssue] = useState<{ id: string; number: string; isCash: boolean } | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set(unbilled.map(e => e.id)));
      setStep('select'); setPreview(null); setPostIssue(null); setPaymentMethod('transfer');
    }
  }, [isOpen]);

  const toggle = (id: string) => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const selIds = Array.from(selected);

  const handlePreview = async (): Promise<boolean> => {
    if (!partnerId) { toast('A projekthez nincs partner rendelve.', 'error'); return false; }
    if (selIds.length === 0) { toast('Válassz ki legalább egy bejegyzést.', 'warning'); return false; }
    setPreviewing(true);
    try {
      const d = await post<any>('/time-entries/preview-invoice', { partnerId, timeEntryIds: selIds, groupBy: 'task' });
      setPreview(d);
      return true;
    } catch (e: any) { toast(e?.message || 'Előnézet sikertelen.', 'error'); return false; }
    finally { setPreviewing(false); }
  };

  const handleGoConfirm = async () => {
    if (!preview) {
      const ok = await handlePreview();
      if (!ok) return;
    }
    setStep('confirm');
  };

  const handleCreate = async () => {
    if (selIds.length === 0 || !partnerId) return;
    setCreating(true);
    try {
      const result = await post<any>('/invoices/from-time-entries', {
        partnerId, timeEntryIds: selIds, groupBy: 'task',
        issueDate, dueDate, paymentMethod,
      });
      setPostIssue({ id: result.invoiceId, number: result.invoiceNumber, isCash: paymentMethod === 'cash' });
      onSuccess();
    } catch (e: any) {
      toast(e?.message || 'Számla létrehozás sikertelen.', 'error');
      setStep('select');
    } finally { setCreating(false); }
  };

  if (postIssue) {
    return (
      <PostIssueModal
        isOpen={true}
        onClose={() => { setPostIssue(null); onClose(); }}
        docType="invoice"
        docId={postIssue.id}
        docNumber={postIssue.number}
        partnerId={partnerId}
        partnerName={partnerName}
        isCash={postIssue.isCash}
      />
    );
  }

  // ── 2. lépés: Megerősítés ──
  if (step === 'confirm') {
    return (
      <Modal isOpen={isOpen} onClose={() => setStep('select')} title="Számla kiállítása">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Figyelmeztetés */}
          <div style={{ display: 'flex', gap: 12, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: '14px 16px' }}>
            <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>⚠️</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>Ez a művelet nem vonható vissza</div>
              <div style={{ fontSize: 12, color: '#a16207', lineHeight: 1.5 }}>
                A számla sorszámmal véglegesítve kerül kiállításra. Utána nem módosítható, csak sztornózással helyesbíthető.
              </div>
            </div>
          </div>

          {/* Számlasorok összefoglalója */}
          {preview && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Számlasorok</div>
              {preview.lines.map((l: any, i: number) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '5px 0', borderBottom: i < preview.lines.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                  <span style={{ fontSize: 13, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: 12 }}>{l.description}</span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: '#1e293b', flexShrink: 0 }}>{l.lineNet.toLocaleString('hu-HU')} Ft</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, marginTop: 4, borderTop: '1px solid #cbd5e1' }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>Bruttó összesen</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#F47B20' }}>{preview.totals.gross.toLocaleString('hu-HU')} Ft</span>
              </div>
            </div>
          )}

          {/* Fizetési mód */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Fizetési mód</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {PM_OPTIONS.map(pm => (
                <button
                  key={pm.value}
                  type="button"
                  onClick={() => setPaymentMethod(pm.value)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                    fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                    border: paymentMethod === pm.value ? '2px solid #1a2540' : '2px solid #e2e8f0',
                    background: paymentMethod === pm.value ? '#1a2540' : '#fff',
                    color: paymentMethod === pm.value ? '#fff' : '#374151',
                  }}
                >
                  <span style={{ fontSize: 18, lineHeight: 1 }}>{pm.icon}</span>
                  {pm.label}
                </button>
              ))}
            </div>
          </div>

          {/* Dátumok */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 500 }}>Kiállítás dátuma</label>
              <input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} className="form-input" style={{ fontSize: 13 }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4, fontWeight: 500 }}>Fizetési határidő</label>
              <input type="date" value={dueDate} onChange={e => setDueDateState(e.target.value)} className="form-input" style={{ fontSize: 13 }} />
            </div>
          </div>

          {/* Gombok */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4, borderTop: '1px solid #f1f5f9' }}>
            <button type="button" onClick={() => setStep('select')}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontSize: 13, cursor: 'pointer' }}>
              ← Vissza
            </button>
            <button type="button" onClick={handleCreate} disabled={creating}
              style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: creating ? '#94a3b8' : '#1a2540', color: '#fff', fontSize: 13, fontWeight: 700, cursor: creating ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              {creating ? '⏳ Kiállítás...' : '📄 Számla kiállítása'}
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // ── 1. lépés: Bejegyzés választó ──
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Számla készítése naplózott órákból">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {!partnerId && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#b91c1c' }}>
            A projekthez nincs partner rendelve — számlát nem lehet kiállítani.
          </div>
        )}

        {unbilled.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, padding: '24px 0' }}>Nincs számlázható, még ki nem számlázott bejegyzés.</p>
        ) : (
          <>
            {/* Fejléc sor */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                Bejegyzések ({selIds.length} / {unbilled.length} kijelölve)
              </span>
              <button type="button"
                onClick={() => setSelected(selected.size === unbilled.length ? new Set() : new Set(unbilled.map(e => e.id)))}
                style={{ fontSize: 12, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                {selected.size === unbilled.length ? 'Összes törlése' : 'Összes kijelölése'}
              </button>
            </div>

            {/* Bejegyzések lista */}
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', maxHeight: 220, overflowY: 'auto' }}>
              {unbilled.map((e, idx) => {
                const taskName = tasks.find(t => t.id === e.task_id)?.title;
                const h = Math.floor(e.duration_minutes / 60);
                const m = e.duration_minutes % 60;
                const net = e.hourly_rate ? Math.round((e.duration_minutes / 60) * e.hourly_rate) : null;
                return (
                  <label key={e.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 12px', cursor: 'pointer',
                    borderBottom: idx < unbilled.length - 1 ? '1px solid #f1f5f9' : 'none',
                    background: selected.has(e.id) ? '#f0f9ff' : '#fff',
                  }}>
                    <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)}
                      style={{ width: 15, height: 15, flexShrink: 0, cursor: 'pointer' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {taskName || e.description || 'Megnevezetlen'}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                        {e.user_name} · {new Date(e.entry_date).toLocaleDateString('hu-HU')}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                        {h > 0 ? `${h}ó ` : ''}{m > 0 ? `${m}p` : ''}
                      </div>
                      {net && <div style={{ fontSize: 11, color: '#64748b' }}>{net.toLocaleString('hu-HU')} Ft</div>}
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Előnézet eredménye */}
            {preview && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Számlasorok előnézete</div>
                {preview.lines.map((l: any, i: number) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: i < preview.lines.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <span style={{ fontSize: 12, color: '#475569', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 12 }}>{l.description}</span>
                    <span style={{ fontSize: 12, fontWeight: 500, flexShrink: 0 }}>{l.lineNet.toLocaleString('hu-HU')} Ft</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 6, marginTop: 4, borderTop: '1px solid #cbd5e1' }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>Bruttó összesen</span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: '#F47B20' }}>{preview.totals.gross.toLocaleString('hu-HU')} Ft</span>
                </div>
              </div>
            )}

            {/* Gombok */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4, borderTop: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
              <button type="button" onClick={onClose}
                style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontSize: 13, cursor: 'pointer' }}>
                Mégse
              </button>
              <button type="button" onClick={handlePreview} disabled={previewing || selIds.length === 0 || !partnerId}
                style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontSize: 13, cursor: 'pointer', opacity: (previewing || selIds.length === 0 || !partnerId) ? 0.45 : 1 }}>
                {previewing ? '⏳ Betöltés...' : '🔍 Előnézet'}
              </button>
              <button type="button" onClick={handleGoConfirm} disabled={previewing || selIds.length === 0 || !partnerId}
                style={{ padding: '9px 18px', borderRadius: 9, border: 'none', background: '#F47B20', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: (previewing || selIds.length === 0 || !partnerId) ? 0.45 : 1 }}>
                Tovább →
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ── ProjectCostTab ──

interface CostReport {
  project: { id: string; name: string; partner: string | null; status: string; startDate: string | null; dueDate: string | null };
  revenue: { totalGross: number; totalNet: number; totalVat: number; byInvoice: Array<{ invoiceId: string; invoiceNumber: string; gross: number; net: number }> };
  costs: {
    directAllocated: { net: number; vat: number; gross: number; byIncomingInvoice: Array<{ incomingInvoiceId: string; invoiceNumber: string; supplierName: string; allocations: Array<{ lineDescription: string; amount: number; mode: string; value: number | null }> }> };
    laborCost: { hours: number; totalCost: number; billableHours: number; nonBillableHours: number; byUser: Array<{ userName: string; hours: number; cost: number }> };
    totalCost: number;
  };
  margin: { amount: number | null; percent: number | null };
  generatedAt: string;
}

function ProjectCostTab({ project }: { project: ProjectDetail }) {
  const [report, setReport] = useState<CostReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    get<CostReport>(`/projects/${project.id}/cost-report`)
      .then(d => { setReport(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [project.id]);

  if (loading) return <div className="py-12 text-center text-gray-400">Betöltés...</div>;
  if (!report) return <div className="py-12 text-center text-gray-400">Nem sikerült betölteni a jelentést.</div>;

  const { revenue, costs, margin } = report;
  const hasRevenue = revenue.totalGross > 0;
  const hasCosts = costs.totalCost > 0;

  return (
    <div className="space-y-6">
      {/* KPI kártyák */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Bevétel (bruttó)</div>
          <div className="text-xl font-bold text-[#1a2540]">{hasRevenue ? `${revenue.totalGross.toLocaleString('hu-HU')} Ft` : '—'}</div>
          <div className="text-xs text-gray-400 mt-0.5">{revenue.byInvoice.length} számla</div>
        </div>
        <div className="rounded-xl border border-gray-200 p-4 text-center">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Összes költség</div>
          <div className="text-xl font-bold text-[#1a2540]">{hasCosts ? `${costs.totalCost.toLocaleString('hu-HU')} Ft` : '—'}</div>
          <div className="text-xs text-gray-400 mt-0.5">munkadíj + allokált</div>
        </div>
        <div className={`rounded-xl border-2 p-4 text-center ${margin.amount !== null && margin.amount >= 0 ? 'border-green-400 bg-green-50' : 'border-red-300 bg-red-50'}`}>
          <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: margin.amount !== null && margin.amount >= 0 ? '#16A34A' : '#DC2626' }}>Fedezet</div>
          <div className="text-xl font-bold" style={{ color: margin.amount !== null && margin.amount >= 0 ? '#16A34A' : '#DC2626' }}>
            {margin.amount !== null ? `${margin.amount.toLocaleString('hu-HU')} Ft` : '—'}
          </div>
          <div className="text-xs mt-0.5" style={{ color: margin.amount !== null && margin.amount >= 0 ? '#16A34A' : '#DC2626' }}>
            {margin.percent !== null ? `${margin.percent.toFixed(1)}% margin` : 'nincs számlázott bevétel'}
          </div>
        </div>
      </div>

      {/* Munkadíj (labor) */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Munkadíj (időnaplózásból)</h3>
            <span className="text-sm text-gray-500">
              {costs.laborCost.hours.toFixed(1)} óra · {costs.laborCost.totalCost > 0 ? `${costs.laborCost.totalCost.toLocaleString('hu-HU')} Ft` : '0 Ft'}
            </span>
          </div>
        </Card.Header>
        {costs.laborCost.byUser.length === 0 ? (
          <Card.Body><p className="text-sm text-gray-400 text-center py-4">Nincs naplózott munkaidő.</p></Card.Body>
        ) : (
          <div className="divide-y divide-gray-100">
            <div className="grid grid-cols-3 gap-2 px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <span>Munkatárs</span><span className="text-right">Óra</span><span className="text-right">Összeg</span>
            </div>
            {costs.laborCost.byUser.map((u, i) => (
              <div key={i} className="grid grid-cols-3 gap-2 px-4 py-2.5 text-sm">
                <span className="text-[#1a2540] font-medium">{u.userName}</span>
                <span className="text-right text-gray-600">{u.hours.toFixed(1)} ó</span>
                <span className="text-right font-semibold">{u.cost > 0 ? `${u.cost.toLocaleString('hu-HU')} Ft` : '—'}</span>
              </div>
            ))}
            <div className="grid grid-cols-3 gap-2 px-4 py-2.5 text-sm bg-blue-50/50">
              <span className="font-semibold text-[#1a2540]">
                Ebből számlázható: {costs.laborCost.billableHours.toFixed(1)} ó / Nem számlázható: {costs.laborCost.nonBillableHours.toFixed(1)} ó
              </span>
              <span />
              <span className="text-right font-bold text-[#1a2540]">{costs.laborCost.totalCost.toLocaleString('hu-HU')} Ft</span>
            </div>
          </div>
        )}
      </Card>

      {/* Allokált anyag/egyéb költségek */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Allokált anyag / egyéb költségek</h3>
            <span className="text-sm text-gray-500">
              {costs.directAllocated.gross > 0 ? `${costs.directAllocated.gross.toLocaleString('hu-HU')} Ft (bruttó)` : '0 Ft'}
            </span>
          </div>
        </Card.Header>
        {costs.directAllocated.byIncomingInvoice.length === 0 ? (
          <Card.Body><p className="text-sm text-gray-400 text-center py-4">Nincs allokált bejövő számla-tétel.</p></Card.Body>
        ) : (
          <div className="divide-y divide-gray-100">
            {costs.directAllocated.byIncomingInvoice.map((inv, i) => (
              <div key={i} className="px-4 py-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-[#1a2540]">{inv.supplierName}</span>
                  <span className="text-xs text-gray-500">{inv.invoiceNumber}</span>
                </div>
                {inv.allocations.map((al, j) => (
                  <div key={j} className="flex items-center justify-between text-xs text-gray-600 ml-2 py-0.5">
                    <span>{al.lineDescription || 'Tétel'}</span>
                    <span className="font-medium">{al.amount.toLocaleString('hu-HU')} Ft</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Bevételek */}
      {hasRevenue && (
        <Card>
          <Card.Header>
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Kimenő számlák (bevétel)</h3>
              <span className="text-sm text-gray-500">{revenue.totalNet.toLocaleString('hu-HU')} Ft nettó</span>
            </div>
          </Card.Header>
          <div className="divide-y divide-gray-100">
            {revenue.byInvoice.map((inv, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                <span className="text-[#1a2540] font-medium">{inv.invoiceNumber}</span>
                <span className="font-semibold">{inv.gross.toLocaleString('hu-HU')} Ft</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ── ProjectDocumentsTab ──

interface ProjectDocument {
  id: string; name: string; kind: string | null; file_size: number | null;
  mime_type: string | null; original_filename: string | null; uploaded_at: string;
  uploader_name: string | null; link_id: string; relation_kind: string | null;
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function fileIcon(mime: string | null): string {
  if (!mime) return '📄';
  if (mime.startsWith('image/')) return '🖼';
  if (mime === 'application/pdf') return '📑';
  if (mime.includes('word') || mime.includes('document')) return '📝';
  if (mime.includes('sheet') || mime.includes('excel') || mime.includes('csv')) return '📊';
  if (mime.includes('zip') || mime.includes('compressed')) return '🗜';
  return '📄';
}

function ProjectDocumentsTab({ project }: { project: ProjectDetail }) {
  const { toast } = useToast();
  const [docs, setDocs] = useState<ProjectDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await get<ProjectDocument[]>(`/projects/${project.id}/documents`); setDocs(Array.isArray(d) ? d : []); }
    catch { /* silent */ }
    setLoading(false);
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name.replace(/\.[^.]+$/, ''));
      const token = getAccessToken() || '';
      const res = await fetch(`/api/projects/${project.id}/documents`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error((err as { error?: string }).error || 'Feltöltés sikertelen.'); }
      toast('Dokumentum feltöltve.', 'success'); await load();
    } catch (e: any) { toast(e?.message || 'Feltöltés sikertelen.', 'error'); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUnlink = async (docId: string, linkId: string) => {
    try { await del(`/documents/${docId}/links/${linkId}`); toast('Kapcsolat eltávolítva.', 'success'); await load(); }
    catch { toast('Hiba az eltávolításkor.', 'error'); }
  };

  const handleDownload = (docId: string, name: string) => {
    const token = getAccessToken() || '';
    const a = document.createElement('a');
    a.href = `/api/documents/${docId}/download`;
    a.download = name;
    a.setAttribute('data-auth', token);
    // Use fetch for auth-gated download
    fetch(`/api/documents/${docId}/download`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => { a.href = URL.createObjectURL(blob); a.click(); URL.revokeObjectURL(a.href); })
      .catch(() => toast('Letöltés sikertelen.', 'error'));
  };

  return (
    <div className="space-y-4">
      {/* Upload */}
      <div
        className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-[#F47B20] transition-colors cursor-pointer"
        onClick={() => !uploading && fileInputRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleUpload(f); }}
      >
        <input ref={fileInputRef} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); }} />
        {uploading ? (
          <div className="text-sm text-gray-500">Feltöltés folyamatban...</div>
        ) : (
          <>
            <div className="text-2xl mb-2">📎</div>
            <div className="text-sm font-medium text-gray-600">Kattints vagy húzd ide a fájlt</div>
            <div className="text-xs text-gray-400 mt-1">Max. 50 MB · PDF, Word, Excel, kép stb.</div>
          </>
        )}
      </div>

      {/* Lista */}
      {loading ? (
        <div className="py-8 text-center text-gray-400">Betöltés...</div>
      ) : docs.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-sm">Nincs csatolt dokumentum.</div>
      ) : (
        <Card>
          <div className="divide-y divide-gray-100">
            {docs.map(doc => (
              <div key={doc.link_id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 group">
                <span className="text-xl shrink-0">{fileIcon(doc.mime_type)}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-[#1a2540] truncate">{doc.name}</div>
                  <div className="text-xs text-gray-400 flex items-center gap-2 flex-wrap">
                    {doc.original_filename && <span>{doc.original_filename}</span>}
                    {doc.file_size && <span>{formatFileSize(doc.file_size)}</span>}
                    {doc.uploader_name && <span>· {doc.uploader_name}</span>}
                    <span>· {new Date(doc.uploaded_at).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleDownload(doc.id, doc.original_filename || doc.name)}
                    className="px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-100 text-gray-600"
                  >↓ Letöltés</button>
                  <button
                    onClick={() => handleUnlink(doc.id, doc.link_id)}
                    className="px-2.5 py-1.5 text-xs rounded-lg border border-red-100 hover:bg-red-50 text-red-500"
                  >✕ Leválaszt</button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ── PlaceholderTab ──

function PlaceholderTab({ text }: { text: string }) {
  return <div className="text-center py-12 text-gray-400 text-sm">{text}</div>;
}

// ── ProjectModal ──

function ProjectModal({ isOpen, onClose, project, users, onSave }: {
  isOpen: boolean; onClose: () => void; project: ProjectDetail | null; users: User[];
  onSave: (data: Record<string, unknown>) => Promise<void>;
}) {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [selectedPartner, setSelectedPartner] = useState<{ id: string; name: string } | null>(null);
  const [ownerUserId, setOwnerUserId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [vatRate, setVatRate] = useState('27');
  const [isBillable, setIsBillable] = useState(false);
  const [hourlyRate, setHourlyRate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(project?.name || '');
      setSelectedPartner(project ? { id: project.partner_id, name: project.partner_name } : null);
      setOwnerUserId(project?.owner_user_id || '');
      setStartDate(project?.start_date?.split('T')[0] || '');
      setDueDate(project?.due_date?.split('T')[0] || '');
      setDescription(project?.description || '');
      setVatRate(String(project?.vat_rate ?? 27));
      setIsBillable(project?.is_billable ?? false);
      setHourlyRate(project?.hourly_rate ? String(project.hourly_rate) : '');
    }
  }, [isOpen, project]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project && !selectedPartner) { toast('Válassz partnert!', 'warning'); return; }
    if (!project && !ownerUserId) { toast('Válassz felelőst!', 'warning'); return; }
    setSaving(true);
    const data: Record<string, unknown> = { name, startDate: startDate || null, dueDate: dueDate || null, description: description || null, vatRate: parseInt(vatRate) || 27, isBillable, hourlyRate: hourlyRate ? parseInt(hourlyRate) : null };
    if (!project) { data.partnerId = selectedPartner!.id; data.ownerUserId = ownerUserId; }
    else if (ownerUserId) { data.ownerUserId = ownerUserId; }
    await onSave(data);
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={project ? 'Projekt szerkesztése' : '+ Új projekt'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="Név *" value={name} onChange={e => setName(e.target.value)} required />
        {!project && (
          <>
            <PartnerAutocomplete label="Partner" required value={selectedPartner} onChange={setSelectedPartner} placeholder="Keress partnert..." />
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Projekt-felelős *</label>
              <select value={ownerUserId} onChange={e => setOwnerUserId(e.target.value)} required className="form-input">
                <option value="">Válassz...</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </>
        )}
        {project && (
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Felelős</label>
            <select value={ownerUserId} onChange={e => setOwnerUserId(e.target.value)} className="form-input">
              <option value="">Nem változtat</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Kezdés" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          <Input label="Határidő" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">ÁFA kulcs</label>
            <select value={vatRate} onChange={e => setVatRate(e.target.value)} className="form-input">
              <option value="0">0% — ÁFA mentes</option>
              <option value="5">5%</option>
              <option value="18">18%</option>
              <option value="27">27%</option>
            </select>
          </div>
          <Input label="Alap óradíj (Ft)" type="number" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder="pl. 8000" />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={isBillable} onChange={e => setIsBillable(e.target.checked)} />
          Számlázható projekt
        </label>
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Leírás</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className="form-input" />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" type="button" onClick={onClose}>Mégse</Button>
          <Button type="submit" loading={saving}>{project ? 'Mentés' : 'Létrehozás'}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── TaskModal ──

function TaskModal({ isOpen, onClose, task, projectId, users, milestones, onSave }: {
  isOpen: boolean; onClose: () => void; task: Task | null; projectId: string; users: User[]; milestones: Milestone[];
  onSave: (data: Record<string, unknown>) => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeUserId, setAssigneeUserId] = useState('');
  const [status, setStatus] = useState('todo');
  const [priority, setPriority] = useState('medium');
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [estimatedHours, setEstimatedHours] = useState('');
  const [milestoneId, setMilestoneId] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTitle(task?.title || ''); setDescription(task?.description || '');
      setAssigneeUserId(task?.assignee_user_id || ''); setStatus(task?.status || 'todo');
      setPriority(task?.priority || 'medium'); setStartDate(task?.start_date?.split('T')[0] || '');
      setDueDate(task?.due_date?.split('T')[0] || ''); setEstimatedHours(task?.estimated_hours ? String(task.estimated_hours) : '');
      setMilestoneId(task?.milestone_id || ''); setHourlyRate(task?.hourly_rate ? String(task.hourly_rate) : '');
    }
  }, [isOpen, task]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true);
    const data: Record<string, unknown> = {
      title, priority,
      description: description || null, assigneeUserId: assigneeUserId || null,
      startDate: startDate || null, dueDate: dueDate || null,
      estimatedHours: estimatedHours ? Number(estimatedHours) : null,
      milestoneId: milestoneId || null, hourlyRate: hourlyRate ? parseInt(hourlyRate) : null,
    };
    if (task) data.status = status;
    await onSave(data); setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={task ? 'Feladat szerkesztése' : '+ Új feladat'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="Cím *" value={title} onChange={e => setTitle(e.target.value)} required />
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Leírás</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} className="form-input" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Felelős</label>
            <select value={assigneeUserId} onChange={e => setAssigneeUserId(e.target.value)} className="form-input">
              <option value="">Nincs felelős</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          {task && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Állapot</label>
              <select value={status} onChange={e => setStatus(e.target.value)} className="form-input">
                <option value="todo">TODO</option><option value="in_progress">Folyamatban</option>
                <option value="review">Ellenőrzés</option><option value="done">Kész</option><option value="cancelled">Törölve</option>
              </select>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Prioritás</label>
            <select value={priority} onChange={e => setPriority(e.target.value)} className="form-input">
              <option value="low">Alacsony</option><option value="medium">Közepes</option>
              <option value="high">Magas</option><option value="urgent">Kritikus</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Mérföldkő</label>
            <select value={milestoneId} onChange={e => setMilestoneId(e.target.value)} className="form-input">
              <option value="">Nincs</option>
              {milestones.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Kezdés" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          <Input label="Határidő" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Idő-becslés (óra)" type="number" value={estimatedHours} onChange={e => setEstimatedHours(e.target.value)} />
          <Input label="Óradíj (Ft)" type="number" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder="pl. 8000" />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" type="button" onClick={onClose}>Mégse</Button>
          <Button type="submit" loading={saving}>{task ? 'Mentés' : 'Létrehozás'}</Button>
        </div>
      </form>
    </Modal>
  );
}

// ── MilestoneModal ──

function MilestoneModal({ isOpen, onClose, milestone, projectId, onSave }: {
  isOpen: boolean; onClose: () => void; milestone: Milestone | null; projectId: string;
  onSave: (data: Record<string, unknown>) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) { setName(milestone?.name || ''); setDueDate(milestone?.due_date?.split('T')[0] || ''); setDescription(milestone?.description || ''); }
  }, [isOpen, milestone]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true);
    await onSave({ name, dueDate: dueDate || null, description: description || null });
    setSaving(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={milestone ? 'Mérföldkő szerkesztése' : '+ Új mérföldkő'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="Név *" value={name} onChange={e => setName(e.target.value)} required />
        <Input label="Határidő" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Leírás</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className="form-input" />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" type="button" onClick={onClose}>Mégse</Button>
          <Button type="submit" loading={saving}>{milestone ? 'Mentés' : 'Létrehozás'}</Button>
        </div>
      </form>
    </Modal>
  );
}
